import {
    POST_LIST_TYPES,
    PostcardActionAttributes,
    PostcardActionButtonsHandlersType,
    PostcardDataType,
    PostComponentOptionsType,
} from "./Post.types";
import {
    CardBoxed,
    CounterLabelTypography,
    CounterUnitTypography,
    CustomizedImageItem,
    ImageLabelTypography,
    PostCardDivider,
    PostCardType,
    PostComponentsSection,
    PostImageGrid,
} from "./Post.styles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PostCommentPreviewSection } from "./PostCommentPreviewSection";
import ImageGrid, { IMAGE_GRID_TYPES, ImageType } from "../ImageGrid/ImageGrid";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import { Box, Stack } from "@mui/material";
import { TextExpander } from "@shared/TextExpander/TextExpander";
import { PostActionButtons } from "./PostActions/PostActionButtons";
import { PostLatestReactedUsers } from "./PostLatestReactedUsers";
import { getLatestReactionsOnPost } from "@infrastructure/Services/PostService";
import { UserType } from "@shared/types";
import { GroupTypeEnum } from "../../Pages/GroupDetailsPage/Group.types";
import { useWindowSize } from "@infrastructure/Hooks/useWindowSize";
import { PostHeaderDefault } from "./Header/PostHeaderDefault";
import { MessageQuestionIcon } from "@icons/MessageQuestionIcon";
import { InfoContainer, InfoTypography } from "./PostForm/PostForm.styles";
import { usePostDetailsModalStore } from "../Modals/PostDetailsModal/PostDetailsModalStore";
import {
    addSubscriber,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { OPEN_PRESENTER } from "./PostForm/events";
import { shortAndShareEntityOnMobile } from "@infrastructure/Helpers/SharingEntity";
import { isDeviceOutputMobileOrMobileApp } from "../../../Domain/DeviceOutputState";
import CountDown from "count-down-react";
import { useInView } from "@infrastructure/Hooks/useInView";
import { PostReactionsListModal } from "../Modals/ServiceModals/ReactionsListModal/PostReactionsListModal";
import { PostPoll } from "./PostPoll/PostPoll";
import { useGroupSelector } from "../GroupSelector/useGroupSelector";
import { TextMarkup } from "@shared/Components/TextMarkup/TextMarkup";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { PollTypesEnum } from "@shared/Types/Polls";
import { LinkoutButton } from "./PostActions/LinkoutButton";
import { LocationButton } from "./PostActions/LocationButton";

const countDownDate = new Date(Date.UTC(2024, 7, 23, 18, 30));
const targetTimestamp = countDownDate.getTime();

export function Post({
    id,
    data,
    showGroupName,
    postActions,
    listType = POST_LIST_TYPES.POST_DETAILS,
    closeButtonCallback,
}: PostComponentOptionsType) {
    const { isMobileScreenSizeFlag } = useWindowSize();

    const postActionOpts: PostcardActionAttributes = {
        postId: data.id,
        postData: data.postData as PostcardDataType,
        groupType: data.group?.type as GroupTypeEnum,
        ...((postActions.onCommentButtonClick
            ? { onCommentButtonClick: postActions.onCommentButtonClick }
            : {}) as PostcardActionButtonsHandlersType),
        showCommentButton: data.pageRelated?.showCommentButton,
        isInsidePostDetails: postActions.isInsidePostDetails,
        commentButtonText: "Kommentieren",
        createReactOnPost: postActions.createReactOnPost,
        onReportButtonClick: onReportButtonClick,
        showReportOption: !data.status.isEditorial,
        onShareButtonClicked,
    };

    const selectedPost: Pick<
        PostComponentOptionsType,
        "id" | "data" | "postActions"
    > = {
        id: id,
        data: data,
        postActions: postActions,
    };

    function onSettingsClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        postActions.setSelectedPost(selectedPost);
        postActions.setIsPostOptionsModalOpen(true);
        postActions.setModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS,
        );
    }

    function onPostGroupTitleClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        listType !== POST_LIST_TYPES.POST_DETAILS &&
            listType !== POST_LIST_TYPES.POST_DETAILS_FULLSCREEN &&
            postActions.onCardContentClickHandler?.(id.toString());
    }

    const [latestReactedUsers, setLatestReactedUsers] = useState([]);
    useEffect(() => {
        if (postActions.isInsidePostDetails && id) {
            getLatestReactionsOnPost(id.toString()).then((data) => {
                setLatestReactedUsers(data.users);
            });
        }
    }, [id, data.postData.postReactions]);

    const cardContentPostOpts = useMemo(() => {
        return {
            ...(postActions.onCardContentClickHandler
                ? {
                      onClick: () => {
                          postActions.onCardContentClickHandler?.(data.id);
                      },
                  }
                : {}),
        };
    }, [postActions, data]);

    function openCommentPreviewOwnerAvatarModal(
        e: React.MouseEvent<HTMLElement>,
    ) {
        e.stopPropagation();
        postActions.onAvatarClick?.(data.latestComment?.createdBy as UserType);
    }

    const isOwnPost = useIsCurrentUserId(data.user.uuid);

    const commentPreviewSectionProps = {
        post: data,
        handleCommentPreviewOptionsClicked:
            postActions.handleCommentPreviewOptionsClicked,
        handleCommentPreviewReportClicked:
            postActions.handleCommentPreviewReportClicked,
        createReactOnPreviewComment: postActions.createReactOnPreviewComment,
        openCommentPreviewOwnerAvatarModal: openCommentPreviewOwnerAvatarModal,
        onCommentButtonClick: postActions.onCommentButtonClick,
        showCommentTeaser: postActions.showCommentTeaser && !isOwnPost,
    };

    function onReportButtonClick() {
        postActions.setSelectedPost &&
            postActions.setSelectedPost(selectedPost);
        if (postActions && postActions.setIsPostOptionsModalOpen) {
            postActions.setIsPostOptionsModalOpen(true);
            postActions.setModalCurrentAction(
                SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT,
            );
        }
    }

    const showDesktopSharingOptions = !isDeviceOutputMobileOrMobileApp();

    const { mainGroupId } = useGroupSelector();

    function onShareButtonClicked() {
        if (showDesktopSharingOptions) {
            postActions.setSelectedPost(selectedPost);
            postActions.setIsPostOptionsModalOpen(true);
            postActions.setModalCurrentAction(
                SERVICE_MODALS_OPTIONS_STATES.SHARING_POST,
            );
        } else {
            mainGroupId &&
                shortAndShareEntityOnMobile(
                    mainGroupId,
                    id.toString(),
                    data.group!.name,
                );
        }
    }

    useEffect(() => {
        if (listType === POST_LIST_TYPES.POST_DETAILS) {
            postActions?.setHeaderComponent?.(
                <PostHeaderDefault
                    openPostAvatarModal={onPostGroupTitleClickHandler}
                    onSettingsClickHandler={onSettingsClickHandler}
                    user={data.user}
                    group={data.group}
                    postData={data.postData}
                    onLocationClick={onPostGroupTitleClickHandler}
                    showGroupName={showGroupName}
                    onPostGroupTitleClickHandler={onPostGroupTitleClickHandler}
                    showClosingButton={true}
                    isHighlighted={data.status.isHighlighted}
                    handleCloseForHeaderComponent={
                        postActions?.handleCloseForHeaderComponent
                    }
                />,
            );
        }
        return () =>
            listType === POST_LIST_TYPES.POST_DETAILS &&
            postActions?.setHeaderComponent?.(undefined);
    }, []);

    function prepareHeader(listType: POST_LIST_TYPES) {
        if (listType !== POST_LIST_TYPES.POST_DETAILS) {
            return (
                <PostHeaderDefault
                    openPostAvatarModal={onPostGroupTitleClickHandler}
                    onSettingsClickHandler={onSettingsClickHandler}
                    user={data.user}
                    group={data.group}
                    postData={data.postData}
                    onLocationClick={onPostGroupTitleClickHandler}
                    showGroupName={showGroupName}
                    onPostGroupTitleClickHandler={onPostGroupTitleClickHandler}
                    showClosingButton={!!closeButtonCallback}
                    handleCloseForHeaderComponent={closeButtonCallback}
                    isHighlighted={data.status.isHighlighted}
                />
            );
        }
    }

    const { isPostDetailsModalOpen } = usePostDetailsModalStore();
    const { ref, inView } = useInView();
    const videoRef = useRef<HTMLVideoElement>();

    const [onlyPlayOnce, setOnlyPlayOnce] = useState(postActions.autoPlayVideo);
    useEffect(() => {
        if (isPostDetailsModalOpen || !inView) {
            if (onlyPlayOnce) {
                videoRef.current?.play();
            } else if (!inView) {
                videoRef.current?.pause();
            }
        }
    }, [inView, isPostDetailsModalOpen, onlyPlayOnce]);

    useEffect(() => {
        if (!onlyPlayOnce) {
            return;
        }

        const handlePlaying = () => {
            setOnlyPlayOnce(false);
        };

        const videoElement = videoRef?.current;

        videoElement?.addEventListener("playing", handlePlaying);

        return () => {
            videoElement?.removeEventListener("playing", handlePlaying);
        };
    }, []);

    useEffect(() => {
        const subscriber = addSubscriber(OPEN_PRESENTER, () => {
            videoRef.current?.pause();
        });
        return () => {
            removeSubscriber(subscriber);
        };
    }, []);

    const [showReactionsModal, setShowReactionsModal] = useState(false);

    return (
        <>
            {showReactionsModal && (
                <PostReactionsListModal
                    open={showReactionsModal}
                    setOpen={setShowReactionsModal}
                    postId={data.id}
                />
            )}
            <CardBoxed
                mt={
                    listType === POST_LIST_TYPES.POST_DETAILS_FULLSCREEN
                        ? "8px"
                        : 0
                }
                isBoxed={data.pageRelated?.boxed}
                {...cardContentPostOpts}
                type={
                    data.status.isHighlighted
                        ? PostCardType.highlighted
                        : PostCardType.normal
                }
            >
                {prepareHeader(listType)}
                <Box>
                    {!data.pageRelated?.hideImages &&
                        data.postData.images &&
                        data.postData.images?.length > 0 && (
                            <PostImageGrid>
                                <ImageGrid
                                    images={
                                        data.postData.images ??
                                        ([] as ImageType[])
                                    }
                                    onClickEach={
                                        !postActions.isInsidePostDetails
                                            ? () => {
                                                  return;
                                              }
                                            : undefined
                                    }
                                    imageGridType={
                                        postActions.isInsidePostDetails &&
                                        isMobileScreenSizeFlag
                                            ? IMAGE_GRID_TYPES.SMALL
                                            : IMAGE_GRID_TYPES.DEFAULT
                                    }
                                />
                            </PostImageGrid>
                        )}
                    {data.postData.videos &&
                        data.postData.videos?.length > 0 &&
                        !data.postData.isMediaUnderProcessing && (
                            <PostImageGrid
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                                ref={ref}
                            >
                                <video
                                    poster={
                                        data.postData.videos[0].thumbnailUrl
                                    }
                                    ref={videoRef}
                                    controls
                                    controlsList={"nodownload noplaybackrate"}
                                    preload={"none"}
                                    playsInline
                                    style={{
                                        background: "black",
                                        maxHeight: "80vh",
                                        width: "100%",
                                        minWidth: "100%",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                >
                                    <source src={data.postData.videos[0].url} />
                                </video>
                            </PostImageGrid>
                        )}
                    {data.postData.isMediaUnderProcessing &&
                        listType === POST_LIST_TYPES.POST_DETAILS && (
                            <InfoContainer>
                                <InfoTypography>
                                    Dein Video wird im Hintergrund verarbeitet
                                    und für die Ausgabe vorbereitet. Dein
                                    Beitrag ist für andere Nutzer erst nach
                                    Fertigstellung sichtbar.
                                </InfoTypography>
                            </InfoContainer>
                        )}
                    {postActions.showGroupCoverImage && data.group?.image && (
                        <PostImageGrid
                            sx={{ display: "flex", position: "relative" }}
                        >
                            <Box
                                component="img"
                                src={data.group?.image}
                                alt={"Group Cover Image"}
                                sx={{
                                    height: "120px",
                                    width: "100%",
                                    borderRadius: "4px",
                                    objectFit: "cover",
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    transform: "translate(-50%, -50%)",
                                    top: "50%",
                                    left: "50%",
                                }}
                            >
                                <MessageQuestionIcon
                                    width={"48px"}
                                    height={"48px"}
                                    color={
                                        "var(--community-color-transparent-white-80)"
                                    }
                                />
                            </Box>
                        </PostImageGrid>
                    )}
                    {!!data.postData.content &&
                        data.postData.poll?.type !==
                            PollTypesEnum.POLL_RATING && (
                            <PostComponentsSection>
                                <TextExpander
                                    lineClampValue={
                                        postActions.isInsidePostDetails ? 20 : 6
                                    }
                                >
                                    <TextMarkup
                                        textToMarkup={data.postData.content}
                                    />
                                </TextExpander>
                            </PostComponentsSection>
                        )}
                    {data.postData?.poll?.pollOptions?.length && (
                        <PostPoll
                            postId={data.id}
                            data={data.postData?.poll}
                            content={data.postData?.content}
                        />
                    )}
                </Box>

                {process.env.REACT_APP_ENV === "prod" && id === 243577 && (
                    <Box marginTop={"8px"}>
                        <CustomizedImageItem
                            image={
                                "https://cdn-public.community.check24.de/static-assets/tippspiel_stadium_large.webp"
                            }
                        >
                            <CountDown
                                date={targetTimestamp}
                                renderer={CountdownRenderer}
                            />
                        </CustomizedImageItem>
                    </Box>
                )}
                {process.env.REACT_APP_ENV === "staging" && id === 81927 && (
                    <Box marginTop={"8px"}>
                        <CustomizedImageItem
                            image={
                                "https://cdn-public.community.check24-test.de/static-assets/tippspiel_stadium_large.webp"
                            }
                        >
                            <CountDown
                                date={targetTimestamp}
                                renderer={CountdownRenderer}
                            />
                        </CustomizedImageItem>
                    </Box>
                )}
                <Stack marginTop="12px">
                    {postActions.isInsidePostDetails ? (
                        data.postData?.linkoutUrl && (
                            <LinkoutButton
                                linkoutUrl={data.postData?.linkoutUrl}
                            />
                        )
                    ) : (
                        <>
                            {data.postData?.location && (
                                <LocationButton
                                    location={data.postData?.location}
                                />
                            )}
                            {data.postData?.linkoutUrl && (
                                <LinkoutButton
                                    linkoutUrl={data.postData?.linkoutUrl}
                                />
                            )}
                        </>
                    )}
                </Stack>
                {!data.pageRelated?.hideActions && (
                    <PostComponentsSection>
                        <PostActionButtons
                            {...postActionOpts}
                            user={data.user}
                            postReactions={data.postData.postReactions}
                            pollsOptions={data.postData.poll?.pollOptions}
                        />
                    </PostComponentsSection>
                )}
                {postActions.isInsidePostDetails &&
                    latestReactedUsers.length > 0 && (
                        <>
                            <PostCardDivider />
                            <Box onClick={() => setShowReactionsModal(true)}>
                                <PostLatestReactedUsers
                                    reactedUsers={latestReactedUsers.slice(
                                        0,
                                        20,
                                    )}
                                    onLatestReactedUserClick={() =>
                                        setShowReactionsModal(true)
                                    }
                                />
                            </Box>
                        </>
                    )}
                {data.pageRelated?.showPreviewComment && (
                    <PostCommentPreviewSection
                        {...commentPreviewSectionProps}
                    />
                )}
            </CardBoxed>
        </>
    );
}

const CountdownRenderer = ({ days, hours, minutes, seconds }) => (
    <Stack
        justifyContent={"center"}
        direction={"column"}
        alignItems={"center"}
        gap={"8px"}
    >
        <Stack
            justifyContent={"center"}
            direction={"row"}
            alignItems={"center"}
            gap={"1.5rem"}
        >
            <Stack direction={"column"} alignItems={"center"}>
                <CounterUnitTypography>{days}</CounterUnitTypography>
                <CounterLabelTypography>Tage</CounterLabelTypography>
            </Stack>
            <Stack direction={"column"} alignItems={"center"}>
                <CounterUnitTypography>{hours}</CounterUnitTypography>
                <CounterLabelTypography>Std.</CounterLabelTypography>
            </Stack>
            <Stack direction={"column"} alignItems={"center"}>
                <CounterUnitTypography>{minutes}</CounterUnitTypography>
                <CounterLabelTypography>Min.</CounterLabelTypography>
            </Stack>
            <Stack direction={"column"} alignItems={"center"}>
                <CounterUnitTypography>{seconds}</CounterUnitTypography>
                <CounterLabelTypography>Sek.</CounterLabelTypography>
            </Stack>
        </Stack>
        <ImageLabelTypography>...bis zur Bundesliga</ImageLabelTypography>
    </Stack>
);
